import { LucideIcon } from "lucide-react";
import { AnimateOnScroll } from "@/components/AnimateOnScroll";

interface ValuePropItem {
  title: string;
  description: string;
  icon: LucideIcon;
}

interface ValuePropsGridProps {
  badge: {
    icon: LucideIcon;
    text: string;
  };
  title: string;
  titleHighlight: string;
  subtitle: string;
  items: ValuePropItem[];
  columns?: 2 | 4;
  accentColor?: string;
}

export const ValuePropsGrid = ({
  badge,
  title,
  titleHighlight,
  subtitle,
  items,
  columns = 2,
  accentColor = "primary",
}: ValuePropsGridProps) => {
  const BadgeIcon = badge.icon;

  const colorClasses: Record<string, { badge: string; title: string; icon: string; border: string }> = {
    primary: {
      badge: "bg-primary/10 text-primary",
      title: "text-primary",
      icon: "bg-primary/10 text-primary",
      border: "hover:border-primary/30",
    },
    emerald: {
      badge: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
      title: "text-emerald-600 dark:text-emerald-400",
      icon: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
      border: "hover:border-emerald-500/30",
    },
    blue: {
      badge: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
      title: "text-blue-600 dark:text-blue-400",
      icon: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
      border: "hover:border-blue-500/30",
    },
  };

  const colors = colorClasses[accentColor] || colorClasses.primary;
  const gridCols = columns === 4 ? "md:grid-cols-4" : "md:grid-cols-2";

  return (
    <section className="py-12 md:py-20 lg:py-24 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6">
        <AnimateOnScroll animation="fade-up">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10 md:mb-12">
            <div className={`inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4 ${colors.badge}`}>
              <BadgeIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>{badge.text}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 sm:mb-4 px-2">
              {title} <span className={colors.title}>{titleHighlight}</span>
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-muted-foreground px-2">{subtitle}</p>
          </div>
        </AnimateOnScroll>

        <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridCols} gap-4 sm:gap-5 md:gap-6 max-w-4xl mx-auto`}>
          {items.map((item, index) => {
            const ItemIcon = item.icon;
            return (
              <AnimateOnScroll key={index} animation="fade-up" delay={index * 100}>
                <div className={`p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl bg-card border border-border transition-all duration-300 h-full ${colors.border}`}>
                  <div className={`w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4 ${colors.icon}`}>
                    <ItemIcon className="w-5 h-5 sm:w-5.5 sm:h-5.5 md:w-6 md:h-6" />
                  </div>
                  <h3 className="text-base sm:text-lg md:text-xl font-semibold text-foreground mb-1.5 sm:mb-2">{item.title}</h3>
                  <p className="text-sm sm:text-base text-muted-foreground">{item.description}</p>
                </div>
              </AnimateOnScroll>
            );
          })}
        </div>
      </div>
    </section>
  );
};
